import { MapId, MapTheme, Vec2, HazardDef, ShortcutDef, SurfaceType } from './types';

// ============================================================
// Map Definition
// ============================================================

export interface MapDef {
  id: MapId;
  name: string;
  description: string;
  controlPoints: Vec2[];
  roadWidth: number;
  wallHeight: number;
  theme: MapTheme;
  startPositions: Array<{ x: number; z: number; yaw: number }>;
  /** Path-fraction positions (0..1) where ramps are placed on the track */
  rampPositions?: number[];
  /** Path fractions for extra coin clusters (optional) */
  coinPositions?: number[];
  /** Track hazards */
  hazards?: HazardDef[];
  /** Shortcut paths */
  shortcuts?: ShortcutDef[];
  /** Surface type affecting physics */
  surfaceType?: SurfaceType;
}

// ============================================================
// Start Grid Generator
// ============================================================

function generateStartGrid(
  baseX: number,
  baseZ: number,
  yaw: number,
  count: number = 12
): Array<{ x: number; z: number; yaw: number }> {
  const positions: Array<{ x: number; z: number; yaw: number }> = [];
  // Cross-track direction (perpendicular to yaw)
  const crossX = -Math.sin(yaw);
  const crossZ = Math.cos(yaw);
  // Backward direction (opposite to facing)
  const backX = -Math.cos(yaw);
  const backZ = -Math.sin(yaw);
  const colSpacing = 3.5;
  const rowSpacing = 4.5;
  const cols = 2;
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const colOffset = (col - (cols - 1) / 2) * colSpacing;
    const rowOffset = row * rowSpacing;
    positions.push({
      x: baseX + crossX * colOffset + backX * rowOffset,
      z: baseZ + crossZ * colOffset + backZ * rowOffset,
      yaw,
    });
  }
  return positions;
}

// ============================================================
// Candy World
// ============================================================

const CANDY_WORLD_POINTS: Vec2[] = [
  { x: 0, z: -50 },
  { x: 30, z: -55 },
  { x: 55, z: -35 },
  { x: 50, z: -5 },
  { x: 60, z: 25 },
  { x: 40, z: 50 },
  { x: 5, z: 45 },
  { x: -30, z: 55 },
  { x: -55, z: 30 },
  { x: -50, z: 0 },
  { x: -55, z: -30 },
  { x: -30, z: -50 },
];

export const CANDY_WORLD: MapDef = {
  id: 'candy_world',
  name: 'Candy World',
  description: 'Race through a sweet wonderland of candy and treats!',
  controlPoints: CANDY_WORLD_POINTS,
  roadWidth: 22,
  wallHeight: 1.5,
  theme: {
    roadColor: 0xffaacc,
    roadStripeColor: 0xff66aa,
    wallColor: 0xff4488,
    groundColor: 0x88dd44,
    skyColor: 0xffccee,
    fogColor: 0xffccee,
    fogNear: 80,
    fogFar: 250,
    ambientColor: 0xffeeff,
    ambientIntensity: 0.6,
    sunColor: 0xffffff,
    sunIntensity: 1.0,
    sunPosition: { x: 50, y: 100, z: 30 },
  },
  startPositions: generateStartGrid(0, -50, 0, 12),
  // Ramps at 20%, 50%, 75% — avoids item boxes at 10%, 35%, 60%, 85%
  rampPositions: [0.2, 0.5, 0.75],
};

// ============================================================
// Giant Bathroom
// ============================================================

const GIANT_BATHROOM_POINTS: Vec2[] = [
  { x: 0, z: -85 },
  { x: 45, z: -85 },
  { x: 85, z: -60 },
  { x: 80, z: -15 },
  { x: 65, z: 25 },
  { x: 85, z: 55 },
  { x: 50, z: 85 },
  { x: -10, z: 80 },
  { x: -50, z: 85 },
  { x: -85, z: 55 },
  { x: -70, z: 15 },
  { x: -85, z: -25 },
  { x: -65, z: -65 },
  { x: -30, z: -85 },
];

export const GIANT_BATHROOM: MapDef = {
  id: 'giant_bathroom',
  name: 'Giant Bathroom',
  description: 'Everything is HUGE! Tiny karts race between giant soap bars and rubber ducks.',
  controlPoints: GIANT_BATHROOM_POINTS,
  roadWidth: 16,
  wallHeight: 3,
  theme: {
    roadColor: 0xddeeff,
    roadStripeColor: 0xaaccee,
    wallColor: 0x88aacc,
    groundColor: 0xeeeeff,
    skyColor: 0xccddee,
    fogColor: 0xccddee,
    fogNear: 100,
    fogFar: 300,
    ambientColor: 0xddeeff,
    ambientIntensity: 0.5,
    sunColor: 0xffffff,
    sunIntensity: 1.2,
    sunPosition: { x: -30, y: 120, z: 60 },
  },
  startPositions: generateStartGrid(0, -85, 0, 12),
  // Ramps at 20%, 50%, 75% — avoids item boxes at 10%, 35%, 60%, 85%
  rampPositions: [0.2, 0.5, 0.75],
};

// ============================================================
// Desert Canyon
// ============================================================

const DESERT_CANYON_POINTS: Vec2[] = [
  { x: 0, z: -80 },
  { x: 35, z: -75 },
  { x: 65, z: -55 },
  { x: 80, z: -25 },
  { x: 85, z: 10 },
  { x: 70, z: 40 },
  { x: 45, z: 65 },
  { x: 10, z: 75 },
  { x: -30, z: 70 },
  { x: -60, z: 50 },
  { x: -75, z: 20 },
  { x: -80, z: -15 },
  { x: -65, z: -50 },
  { x: -35, z: -75 },
];

export const DESERT_CANYON: MapDef = {
  id: 'desert_canyon',
  name: 'Desert Canyon',
  description: 'Blaze through scorching canyons dodging rolling boulders and sand dunes!',
  controlPoints: DESERT_CANYON_POINTS,
  roadWidth: 14,
  wallHeight: 2,
  theme: {
    roadColor: 0xd4a052,
    roadStripeColor: 0xc48940,
    wallColor: 0xb87830,
    groundColor: 0xe8c878,
    skyColor: 0x87ceeb,
    fogColor: 0xe8d8a0,
    fogNear: 100,
    fogFar: 280,
    ambientColor: 0xffeebb,
    ambientIntensity: 0.7,
    sunColor: 0xfff0d0,
    sunIntensity: 1.3,
    sunPosition: { x: 60, y: 100, z: 40 },
  },
  startPositions: generateStartGrid(0, -80, 0, 12),
  rampPositions: [0.15, 0.45, 0.7],
  surfaceType: 'sand',
  hazards: [
    { type: 'boulder', pathFraction: 0.25, lateralOffset: 0, behavior: 'moving', speed: 3 },
    { type: 'boulder', pathFraction: 0.55, lateralOffset: 0.3, behavior: 'moving', speed: 4 },
    { type: 'boulder', pathFraction: 0.8, lateralOffset: -0.2, behavior: 'moving', speed: 2.5 },
  ],
  shortcuts: [
    {
      entryFraction: 0.3,
      exitFraction: 0.45,
      controlPoints: [
        { x: 75, z: -10 },
        { x: 80, z: 20 },
        { x: 65, z: 45 },
      ],
      width: 10,
    },
  ],
};

// ============================================================
// Space Station
// ============================================================

const SPACE_STATION_POINTS: Vec2[] = [
  { x: 0, z: -70 },
  { x: 40, z: -65 },
  { x: 70, z: -35 },
  { x: 65, z: 10 },
  { x: 75, z: 45 },
  { x: 40, z: 70 },
  { x: -10, z: 65 },
  { x: -50, z: 70 },
  { x: -75, z: 35 },
  { x: -70, z: -10 },
  { x: -60, z: -45 },
  { x: -30, z: -70 },
];

export const SPACE_STATION: MapDef = {
  id: 'space_station',
  name: 'Space Station',
  description: 'Race through a futuristic orbital station with neon-lit corridors and laser gates!',
  controlPoints: SPACE_STATION_POINTS,
  roadWidth: 13,
  wallHeight: 2.5,
  theme: {
    roadColor: 0x333366,
    roadStripeColor: 0x6666ff,
    wallColor: 0x222244,
    groundColor: 0x111133,
    skyColor: 0x0a0a2e,
    fogColor: 0x0a0a2e,
    fogNear: 60,
    fogFar: 200,
    ambientColor: 0x4444aa,
    ambientIntensity: 0.4,
    sunColor: 0xaaaaff,
    sunIntensity: 0.8,
    sunPosition: { x: 0, y: 80, z: 0 },
  },
  startPositions: generateStartGrid(0, -70, 0, 12),
  rampPositions: [0.1, 0.4, 0.65],
  surfaceType: 'space',
  hazards: [
    { type: 'laser_gate', pathFraction: 0.2, lateralOffset: 0, behavior: 'timed', onTime: 3, offTime: 2 },
    { type: 'laser_gate', pathFraction: 0.5, lateralOffset: 0, behavior: 'timed', onTime: 2.5, offTime: 3 },
    { type: 'laser_gate', pathFraction: 0.75, lateralOffset: 0, behavior: 'timed', onTime: 2, offTime: 2 },
  ],
};

// ============================================================
// Haunted Mansion
// ============================================================

const HAUNTED_MANSION_POINTS: Vec2[] = [
  { x: 0, z: -75 },
  { x: 30, z: -80 },
  { x: 60, z: -60 },
  { x: 75, z: -30 },
  { x: 70, z: 5 },
  { x: 55, z: 35 },
  { x: 30, z: 60 },
  { x: -5, z: 70 },
  { x: -40, z: 65 },
  { x: -65, z: 40 },
  { x: -75, z: 5 },
  { x: -70, z: -35 },
  { x: -45, z: -65 },
];

export const HAUNTED_MANSION: MapDef = {
  id: 'haunted_mansion',
  name: 'Haunted Mansion',
  description: 'Navigate the eerie halls of a cursed mansion where ghosts patrol the corridors!',
  controlPoints: HAUNTED_MANSION_POINTS,
  roadWidth: 13,
  wallHeight: 3,
  theme: {
    roadColor: 0x443355,
    roadStripeColor: 0x665577,
    wallColor: 0x332244,
    groundColor: 0x221133,
    skyColor: 0x1a0a2e,
    fogColor: 0x2a1a3e,
    fogNear: 40,
    fogFar: 150,
    ambientColor: 0x6644aa,
    ambientIntensity: 0.3,
    sunColor: 0xddbbff,
    sunIntensity: 0.5,
    sunPosition: { x: -40, y: 60, z: 30 },
  },
  startPositions: generateStartGrid(0, -75, 0, 12),
  rampPositions: [0.25, 0.55, 0.85],
  surfaceType: 'normal',
  hazards: [
    { type: 'ghost', pathFraction: 0.2, lateralOffset: 0.4, behavior: 'moving', speed: 6 },
    { type: 'ghost', pathFraction: 0.5, lateralOffset: -0.3, behavior: 'moving', speed: 5 },
    { type: 'ghost', pathFraction: 0.8, lateralOffset: 0, behavior: 'moving', speed: 7 },
  ],
};

// ============================================================
// Ice World
// ============================================================

const ICE_WORLD_POINTS: Vec2[] = [
  { x: 0, z: -70 },
  { x: 40, z: -68 },
  { x: 72, z: -45 },
  { x: 80, z: -5 },
  { x: 68, z: 35 },
  { x: 40, z: 65 },
  { x: -5, z: 72 },
  { x: -45, z: 65 },
  { x: -72, z: 35 },
  { x: -78, z: -5 },
  { x: -65, z: -42 },
  { x: -35, z: -68 },
];

export const ICE_WORLD: MapDef = {
  id: 'ice_world',
  name: 'Ice World',
  description: 'Slide across frozen lakes and icy roads in this slippery winter wonderland!',
  controlPoints: ICE_WORLD_POINTS,
  roadWidth: 16,
  wallHeight: 1.5,
  theme: {
    roadColor: 0xaaddee,
    roadStripeColor: 0x88bbdd,
    wallColor: 0x99ccee,
    groundColor: 0xeef5ff,
    skyColor: 0xccddee,
    fogColor: 0xddeeff,
    fogNear: 80,
    fogFar: 250,
    ambientColor: 0xccddff,
    ambientIntensity: 0.6,
    sunColor: 0xffffff,
    sunIntensity: 0.9,
    sunPosition: { x: 40, y: 90, z: 50 },
  },
  startPositions: generateStartGrid(0, -70, 0, 12),
  rampPositions: [0.2, 0.5, 0.75],
  surfaceType: 'ice',
  hazards: [
    { type: 'ice_crack', pathFraction: 0.3, lateralOffset: 0.2, behavior: 'static' },
    { type: 'ice_crack', pathFraction: 0.6, lateralOffset: -0.3, behavior: 'static' },
    { type: 'ice_crack', pathFraction: 0.85, lateralOffset: 0, behavior: 'static' },
  ],
  shortcuts: [
    {
      entryFraction: 0.55,
      exitFraction: 0.7,
      controlPoints: [
        { x: -10, z: 72 },
        { x: -35, z: 68 },
        { x: -55, z: 50 },
      ],
      width: 12,
    },
  ],
};

// ============================================================
// Registry
// ============================================================

export const ALL_MAPS: MapDef[] = [
  CANDY_WORLD,
  GIANT_BATHROOM,
  DESERT_CANYON,
  SPACE_STATION,
  HAUNTED_MANSION,
  ICE_WORLD,
];

export function getMapDef(id: MapId): MapDef {
  return ALL_MAPS.find((m) => m.id === id) || CANDY_WORLD;
}
